<template>
  <div>
    <h3 ref="intro">
      Cartesian Components of a Vector
    </h3>
    <p>
      Consider a 2D Cartesian coordinate system x-y defined by a set of mutually orthogonal unit vectors , which are also known as basis vectors of the coordinate system.
      A vector, say \(\mathbf r\), in such a coordinate system can be represented by means of a doublet of scalars:
      $$\mathbf r = r_x \mathbf x + r_y \mathbf y  \tag{1}$$
      The scalars \(r_x\) and \(r_y\) are called the components of vector \(\mathbf r\) in the Cartesian basis defined by unit vectors \(\mathbf x\) and \(\mathbf y\).
      Upon taking the scalar product of \(\mathbf x\) and \(\mathbf r\), we obtain:
      $$\mathbf r \cdot \mathbf x = r_x \mathbf x \cdot \mathbf x  + r_y \mathbf y \cdot \mathbf x  \tag{2}$$
      Note that the basis vectors \(\mathbf x \) and \(\mathbf y \)are mutually orthogonal, i.e.,
      $$ \mathbf x \cdot \mathbf y =  \mathbf y \cdot \mathbf z = \mathbf z \cdot \mathbf x = 0 \tag{3}$$
      Further, since the basis vectors are unit vectors, we obtain:
      $$ \mathbf x \cdot \mathbf x = \mathbf y \cdot \mathbf y = \mathbf z \cdot \mathbf z =1 \tag{4}$$
      Using the results from equations (2) and (3) in equation (4), we obtain
      $$r_x = \mathbf r \cdot \mathbf x \tag{5}$$
      Following a similar process, we can show that:
      $$r_y = \mathbf r \cdot \mathbf y \tag{6}$$
      Note that although the vector \(\mathbf r \) is independent of the choice of coordinate system, however, the components \(r_x\), and \(r_y\) are not.
    </p>
    <h3 ref="transformation">
      Transformation of Coordinates
    </h3>
    <p>
      Now, consider another Two-D Cartesian coordinate system x'-y' which is rotated by an angle \(\theta \) w.r.t. original coordinate system x-y, as shown in the figure below.
    </p>
    <v-layout justify-center>
      <v-img
        max-height="300px"
        max-width="300px"
        src="assets/transform.png"
        contain
      />
    </v-layout>
    <p>
      The rotated coordinate system is defined by basis vectors \(\mathbf x'\) and \(\mathbf y'\) which are related to original basis vectors as follows:
      $$\mathbf x' = \cos \theta\ \mathbf x + \sin \theta\ \mathbf y \tag{7}$$
      and
      $$\mathbf y' = -\sin \theta \ \mathbf x + \cos \theta \ \mathbf y \tag{8}$$
      Let's say the components of vector \(\mathbf r\) in the new Cartesian basis
      $$\mathbf r = r_x' \mathbf x' + r_y' \mathbf y' \tag{5}$$
      Taking the dot product with \(\mathbf x'\) gives
      $$ r_{x'} = \mathbf r \cdot \mathbf x' = r_x \mathbf x \cdot \mathbf x' + r_y \mathbf y \cdot \mathbf x'= r_x \cos \theta + r_y \sin \theta \tag{9}$$
      Upon taking the dot product with \(\mathbf y'\) gives
      $$ r_{y'} = \mathbf r \cdot \mathbf y' = r_x \mathbf x \cdot \mathbf y' + r_y \mathbf y \cdot \mathbf y'= -r_x \sin \theta + r_y \cos \theta \tag{10}$$
    </p>
    <h3 ref="matrix">
      Transformation as a Matrix Operation
    </h3>
    <p>
      In tensorial notation, the above transformation law can be written as:
      $$\begin{bmatrix} r_{x'} \\ r_{y'} \end{bmatrix} = \begin{bmatrix} \cos \theta & \sin \theta \\ -\sin \theta & \cos \theta \end{bmatrix}\cdot \begin{bmatrix} r_{x} \\ r_{y} \end{bmatrix}$$
    </p>
    <h3 ref="playgraph">
      MagicGraph: Vector Transformation Under Rotation of Coordinate System
    </h3>
    <v-responsive
      :aspect-ratio="1"
    >
      <v-layout justify-center>
        <div id="jxgbox1" class="edliy-box-about" />
      </v-layout>
      <v-layout justify-center>
        <v-hover v-slot:default="{ hover }">
          <v-btn :color="hover ? '#757575' : '#2E2E2E' "
                 large
                 depressed
                 @click="$router.push({name: 'mathematics'})"
          >
            <v-img
              src="/assets/masswhite.svg"
              aspect-ratio="1"
              contain
            />
          </v-btn>
        </v-hover>
        <v-hover v-slot:default="{ hover }">
          <v-btn :color="hover ? '#757575' : '#2E2E2E' "
                 large
                 depressed
                 @click="$router.push({name: 'home'})"
          >
            <v-img
              src="/assets/edliy.svg"
              aspect-ratio="1"
              width="5px"
              contain
            />
          </v-btn>
        </v-hover>
      </v-layout>
    </v-responsive>
  </div>
</template>

<script>
import Boxes from './Boxes.js'
export default {
  name: 'ForcesStresses',
  created: function () {
    // Store mutations
    let title = 'Vector Transformation in Two-D';
    this.$store.commit('navigation/changeTitle', title);
    this.$store.commit('navigation/changeMenu', title);
    let newTopics = [
      {title: 'Vectors: Introduction', img:'assets/number-1.svg', action: () => this.goto('intro')},
      {title: 'Cartesian Components of a Vector', img:'assets/number-2.svg',action: () => this.goto('transformation')},
      {title: 'Transformation of Coordinates',img:'assets/number-3.svg', action: () => this.goto('matrix')},
      {title: 'PlayGraph',img:'assets/touch.svg', action: () => this.goto('playgraph')},
    ];
    this.$store.commit('navigation/replaceTopics', newTopics);
    let newshowhome = false;
    this.$store.commit('navigation/toggleshowhome', newshowhome);
    let newMath =true;
    this.$store.commit('navigation/replaceMath', newMath);
  },
  mounted () {
    MathJax.Hub.Queue(["Typeset", MathJax.Hub]);
    Boxes.box1();
  },
  methods: {
  }
}
</script>

<style lang="scss">
@import 'src/styles/edliy-box.scss';
@import 'src/styles/subtopic-menu.scss';
@import 'src/styles/edliy-box-about.scss';
@import 'src/styles/screen-sizes.scss';
</style>
